import { cn } from "@/design-system/lib/utils";
import {
  calendarDaysUntil,
  formatCalendarDate,
} from "@/utils/datetime";

interface Props {
  /** ISO timestamp; null means no due date — pill renders nothing. */
  dueDate: string | null | undefined;
  /** When true, hide the "Done" task by rendering nothing (caller decides). */
  className?: string;
}

interface PillState {
  label: string;
  className: string;
  ariaLabel: string;
}

/** Computes the visual state — exported for unit testing. */
export function computeDueState(
  iso: string | null | undefined,
  now: Date = new Date(),
): PillState | null {
  if (!iso) return null;
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return null;

  // This logic WAS the reference implementation — the only one in the codebase
  // that got the calendar arithmetic right — and it has been lifted verbatim
  // into `calendarDaysUntil` so the other six countdowns could adopt it.
  // Delegating rather than keeping a private copy is the point: a rule that
  // still had two homes would be a rule that can still disagree with itself.
  const diffDays = calendarDaysUntil(due, now);
  if (diffDays === null) return null;

  if (diffDays < 0) {
    const overdue = Math.abs(diffDays);
    return {
      label: `${overdue}d overdue`,
      className: "bg-status-error text-white-pure",
      ariaLabel: `Overdue by ${overdue} day${overdue === 1 ? "" : "s"}`,
    };
  }
  if (diffDays === 0) {
    return {
      label: "Due today",
      className: "bg-gold text-onyx",
      ariaLabel: "Due today",
    };
  }
  if (diffDays <= 3) {
    return {
      label: `Due in ${diffDays} day${diffDays === 1 ? "" : "s"}`,
      className: "bg-transparent text-gold-shadow border border-gold-shadow",
      ariaLabel: `Due in ${diffDays} days`,
    };
  }
  const formatted = formatCalendarDate(due, {
    year: undefined,
    month: "short",
    day: "numeric",
  });
  return {
    label: `Due ${formatted}`,
    className: "bg-transparent text-gray-mid border border-border-light",
    ariaLabel: `Due ${formatted}`,
  };
}

export function TaskDueDatePill({ dueDate, className }: Props) {
  const state = computeDueState(dueDate);
  if (!state) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center px-space-2 py-[3px] rounded-sm",
        "font-display text-[10px] font-bold uppercase tracking-[0.16em]",
        state.className,
        className,
      )}
      aria-label={state.ariaLabel}
    >
      {state.label}
    </span>
  );
}
